import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_URL from '../api';
import logoMark from '../assets/images/logo.jpg';
import { Reveal, TabNav, Modal } from '../ui';
import EntitlementCards from '../billing/EntitlementCards';
import PricingPlans from '../billing/PricingPlans';
import CheckoutModal from '../billing/CheckoutModal';
import { fetchPackages, fetchEntitlements, formatInr, formatDate } from '../billing/checkout';
import '../billing/billing.css';

/* ==================================================================
   RecruiterBilling
   ------------------------------------------------------------------
   Everything a recruiter needs to answer "what do I have, what did I
   pay, and who can I still reach?" — balances, package history,
   payment history, and the profiles they have unlocked.

   Deliberately NOT a second dashboard: it has no jobs, no applicants
   and no candidate search. It is the billing surface the existing
   recruiter dashboard links out to.
   ================================================================== */

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const STATUS_PILL = {
  paid: { label: 'Paid', cls: 'bl-pill--active' },
  created: { label: 'Pending', cls: 'bl-pill--none' },
  failed: { label: 'Failed', cls: 'bl-pill--danger' },
  refunded: { label: 'Refunded', cls: 'bl-pill--expired' },
};

function StatusPill({ status }) {
  const meta = STATUS_PILL[status] || STATUS_PILL.created;
  return <span className={`bl-pill ${meta.cls}`}>{meta.label}</span>;
}

function EmptyRow({ children }) {
  return (
    <div className="bl-empty">
      <div className="bl-empty-title">Nothing here yet</div>
      <p>{children}</p>
    </div>
  );
}

export default function RecruiterBilling({ onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState('overview');
  const [entitlements, setEntitlements] = useState(null);
  const [packages, setPackages] = useState(null);
  const [payments, setPayments] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [unlocks, setUnlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [buyOpen, setBuyOpen] = useState(false);
  const [buyProduct, setBuyProduct] = useState(null);
  const [checkout, setCheckout] = useState({ open: false, pkg: null });

  const load = useCallback(async () => {
    setError(null);
    try {
      const [ent, pkgs, pay, pur, unl] = await Promise.all([
        fetchEntitlements(),
        fetchPackages(),
        axios.get(`${API_URL}/api/billing/payments?limit=50`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/billing/purchases`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/billing/unlocks`, { headers: authHeaders() }),
      ]);
      setEntitlements(ent);
      setPackages(pkgs);
      setPayments(pay.data.payments || []);
      setPurchases(pur.data.purchases || []);
      setUnlocks(unl.data.unlocks || []);
    } catch {
      setError('We could not load your billing details. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ?buy=profile / ?buy=job — how the paywall elsewhere in the product deep-links
  // straight to the packages that unblock whatever the recruiter was doing.
  useEffect(() => {
    const buy = searchParams.get('buy');
    if (buy === 'profile' || buy === 'job') {
      setBuyProduct(buy);
      setBuyOpen(true);
      searchParams.delete('buy');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const openBuy = (product) => {
    setBuyProduct(product || null);
    setBuyOpen(true);
  };

  const handleSelect = (pkg) => {
    setBuyOpen(false);
    setCheckout({ open: true, pkg });
  };

  const activeUnlocks = unlocks.filter((u) => u.active).length;

  return (
    <div className="bl-page">
      <nav className="fs-nav">
        <button className="fs-brand" onClick={() => navigate('/recruiter/dashboard')}>
          <img src={logoMark} alt="" className="brand-mark" />
          <div>
            <div className="fs-brand-title">HYRSKILL</div>
            <div className="fs-brand-sub">Billing &amp; Credits</div>
          </div>
        </button>
        <div className="fs-nav-actions">
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/pricing')}>Pricing</button>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/recruiter/dashboard')}>← Dashboard</button>
          {onLogout && <button className="btn btn-secondary btn-sm" onClick={onLogout}>Sign Out</button>}
        </div>
      </nav>

      <TabNav
        className="rd-tabs"
        tabClassName="rd-tab"
        ariaLabel="Billing sections"
        active={tab}
        onChange={setTab}
        items={[
          { id: 'overview', label: 'Overview' },
          { id: 'packages', label: 'Packages', badge: purchases.length || undefined },
          { id: 'payments', label: 'Payments', badge: payments.length || undefined },
          { id: 'unlocks', label: 'Unlocked Students', badge: unlocks.length || undefined },
        ]}
      />

      <div className="bl-body ui-view" style={{ paddingTop: 28 }}>
        {error && (
          <div className="bl-banner bl-banner--error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12.01" y2="16.5" />
            </svg>
            <div className="bl-banner-body">{error}</div>
          </div>
        )}

        {/* ── Overview ── */}
        {tab === 'overview' && (
          <>
            {entitlements?.profile?.expired && (
              <div className="bl-banner bl-banner--warn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                </svg>
                <div className="bl-banner-body">
                  <strong>Your profile access has expired</strong>
                  Students you unlocked under that package are no longer reachable, and any
                  unused credits from it can’t be spent. Buying a new package restores access
                  to students you unlock from now on.
                  <br />
                  <button type="button" className="bl-banner-cta" onClick={() => openBuy('profile')}>
                    View profile packages
                  </button>
                </div>
              </div>
            )}

            <Reveal className="bl-section">
              <div className="bl-section-head">
                <div className="bl-section-title">
                  <div>
                    <h2>Your credits</h2>
                    <p className="bl-section-sub">What you can spend right now.</p>
                  </div>
                </div>
              </div>
              <EntitlementCards
                entitlements={entitlements}
                loading={loading}
                onBuy={openBuy}
                onFindStudents={() => navigate('/recruiter/find-students')}
                onPostJob={() => navigate('/recruiter/post-job')}
              />
            </Reveal>

            {!loading && (
              <Reveal className="bl-section" delay={60}>
                <div className="bl-section-head">
                  <div className="bl-section-title">
                    <div>
                      <h2>At a glance</h2>
                    </div>
                  </div>
                </div>
                <div className="bl-ent-grid">
                  <div className="bl-ent">
                    <span className="bl-ent-label">Total spent</span>
                    <div className="bl-ent-count">
                      <span className="bl-ent-big bl-num">
                        {formatInr(payments
                          .filter((p) => p.status === 'paid')
                          .reduce((sum, p) => sum + p.amount_paise, 0))}
                      </span>
                    </div>
                    <p className="bl-ent-caption">
                      Across {payments.filter((p) => p.status === 'paid').length} successful{' '}
                      {payments.filter((p) => p.status === 'paid').length === 1 ? 'payment' : 'payments'}
                    </p>
                  </div>
                  <div className="bl-ent">
                    <span className="bl-ent-label">Students you can reach</span>
                    <div className="bl-ent-count">
                      <span className="bl-ent-big bl-num">{activeUnlocks}</span>
                      {unlocks.length !== activeUnlocks && (
                        <span className="bl-ent-of">of {unlocks.length} unlocked</span>
                      )}
                    </div>
                    <p className="bl-ent-caption">
                      {unlocks.length === activeUnlocks
                        ? 'All unlocked profiles are currently accessible'
                        : `${unlocks.length - activeUnlocks} no longer accessible — their package has expired`}
                    </p>
                  </div>
                </div>
              </Reveal>
            )}
          </>
        )}

        {/* ── Packages ── */}
        {tab === 'packages' && (
          <Reveal className="bl-section">
            <div className="bl-section-head">
              <div className="bl-section-title">
                <div>
                  <h2>Packages you’ve bought</h2>
                  <p className="bl-section-sub">
                    Every purchase is kept separately. Buying again adds its credits and extends
                    your access — it never shortens what you already have.
                  </p>
                </div>
              </div>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => openBuy()}>
                Buy a package
              </button>
            </div>

            {purchases.length === 0 ? (
              <EmptyRow>Your purchased packages will be listed here.</EmptyRow>
            ) : (
              <div className="bl-table-wrap">
                <table className="bl-table">
                  <thead>
                    <tr>
                      <th>Package</th>
                      <th>Type</th>
                      <th>Credits</th>
                      <th>Amount</th>
                      <th>Purchased</th>
                      <th>Valid until</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((p) => (
                      <tr key={p.id}>
                        <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{p.package_name}</td>
                        <td>{p.product === 'profile' ? 'Profile access' : 'Job posts'}</td>
                        <td className="bl-num">{p.credits}</td>
                        <td className="bl-num">{p.price_inr}</td>
                        <td>{formatDate(p.purchased_at)}</td>
                        <td>{formatDate(p.expires_at)}</td>
                        <td>
                          {p.status === 'refunded' ? <span className="bl-pill bl-pill--expired">Refunded</span>
                            : p.status === 'revoked' ? <span className="bl-pill bl-pill--danger">Revoked</span>
                              : p.expired ? <span className="bl-pill bl-pill--expired">Expired</span>
                                : <span className="bl-pill bl-pill--active">Active</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Reveal>
        )}

        {/* ── Payments ── */}
        {tab === 'payments' && (
          <Reveal className="bl-section">
            <div className="bl-section-head">
              <div className="bl-section-title">
                <div>
                  <h2>Payment history</h2>
                  <p className="bl-section-sub">All prices include GST.</p>
                </div>
              </div>
            </div>

            {payments.length === 0 ? (
              <EmptyRow>Your payments will appear here once you buy a package.</EmptyRow>
            ) : (
              <div className="bl-table-wrap">
                <table className="bl-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Package</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Order ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td>{formatDate(p.paid_at || p.created_at)}</td>
                        <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                          {p.package_name}
                          <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>
                            {p.credits} {p.product === 'profile' ? 'profile credits' : 'job posts'}
                          </span>
                        </td>
                        <td className="bl-num">{p.price_inr}</td>
                        <td>
                          <StatusPill status={p.status} />
                          {p.failure_reason && (
                            <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3 }}>
                              {p.failure_reason}
                            </span>
                          )}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace' }}>
                          {p.razorpay_order_id}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Reveal>
        )}

        {/* ── Unlocked students ── */}
        {tab === 'unlocks' && (
          <Reveal className="bl-section">
            <div className="bl-section-head">
              <div className="bl-section-title">
                <div>
                  <h2>Students you’ve unlocked</h2>
                  <p className="bl-section-sub">
                    Contact details stay available while the package that unlocked them is valid.
                  </p>
                </div>
              </div>
            </div>

            {unlocks.length === 0 ? (
              <EmptyRow>Students you unlock will be listed here.</EmptyRow>
            ) : (
              <div className="bl-table-wrap">
                <table className="bl-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>College</th>
                      <th>Unlocked</th>
                      <th>Access until</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {unlocks.map((u) => (
                      <tr key={u.student_id}>
                        <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{u.name}</td>
                        <td>{u.college || '—'}</td>
                        <td>{formatDate(u.unlocked_at)}</td>
                        <td>
                          {u.access_expires_at
                            ? formatDate(u.access_expires_at)
                            : (u.credit_type === 'free' ? 'No expiry' : '—')}
                        </td>
                        <td>
                          {u.active
                            ? <span className="bl-pill bl-pill--active">Accessible</span>
                            : <span className="bl-pill bl-pill--expired">Access ended</span>}
                        </td>
                        <td>
                          {u.active && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => window.open(`/student/${u.student_id}`, '_blank', 'noopener')}
                            >
                              View
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Reveal>
        )}
      </div>

      {/* Package picker. Opens scoped to one product when the recruiter got here
          from a specific paywall, and shows both when they just want to buy. */}
      <Modal
        open={buyOpen}
        onClose={() => setBuyOpen(false)}
        maxWidth={buyProduct ? 900 : 1000}
        labelledBy="bl-buy-title"
      >
        <div style={{ padding: '28px 26px 24px' }}>
          <h2 id="bl-buy-title" style={{ fontSize: 20, marginBottom: 6 }}>
            {buyProduct === 'job' ? 'Buy job posts'
              : buyProduct === 'profile' ? 'Buy profile credits'
                : 'Choose a package'}
          </h2>
          <p style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 22 }}>
            One-time payment, inclusive of GST. Credits are added the moment your payment is confirmed.
          </p>
          {packages && (
            <PricingPlans
              packages={packages}
              onSelect={handleSelect}
              disabled={packages.payments_enabled === false}
              products={buyProduct ? [buyProduct] : ['profile', 'job']}
              compact
            />
          )}
        </div>
      </Modal>

      <CheckoutModal
        open={checkout.open}
        pkg={checkout.pkg}
        onClose={() => { setCheckout({ open: false, pkg: null }); load(); }}
        onSuccess={load}
      />
    </div>
  );
}
